import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCollaboratorEarningsOverview,
  fetchAllCollaboratorEarnings,
} from "@/api/collaborator";
import { useSessionState } from "@/hooks/useSessionState";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  DollarSign,
  Search,
  Filter,
  Download,
  ExternalLink,
  Tag,
  Calendar,
  TrendingUp,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";

interface CollaboratorEarning {
  id: number;
  collaborator_id: number;
  promotion_id: number;
  user_subscription_id: number;
  amount_earned: number;
  commission_type_at_time_of_transaction: "percentage" | "fixed";
  commission_at_time_of_transaction: number;
  payout_id: number | null;
  created_at: string;
  collaborator: {
    id: number;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
  promotion: {
    id: number;
    name: string;
    code: string;
  };
  user_subscription: {
    id: number;
    price: number;
    currency_code: string;
    user_id: number;
    subscription_plan_id: number;
    status: string;
    subscription_start: string;
  };
  customer: {
    id: number;
    name: string | null;
    email: string | null;
  };
  payout?: {
    id: number;
    status: "pending" | "completed" | "failed";
    payout_date: string | null;
  };
}

const CollaboratorEarningsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [earnings, setEarnings] = useState<CollaboratorEarning[]>([]);
  const [filteredEarnings, setFilteredEarnings] = useState<
    CollaboratorEarning[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useSessionState("collaboratorEarningsList.searchTerm", "");
  const [statusFilter, setStatusFilter] = useSessionState("collaboratorEarningsList.statusFilter", "all");
  const [collaboratorFilter, setCollaboratorFilter] = useSessionState("collaboratorEarningsList.collaboratorFilter", "all");
  const [collaborators, setCollaborators] = useState<
    Array<{ id: number; name: string | null }>
  >([]);

  // Pagination
  const [currentPage, setCurrentPage] = useSessionState("collaboratorEarningsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("collaboratorEarningsList.pageSize", 10);

  // Fetch earnings overview (for cards)
  const { data: earningsOverview } = useQuery({
    queryKey: ["collaboratorEarningsOverview"],
    queryFn: fetchCollaboratorEarningsOverview,
  });

  // Fetch all earnings data
  const { data: allEarningsData, isLoading: isLoadingEarnings } = useQuery({
    queryKey: ["allCollaboratorEarnings"],
    queryFn: fetchAllCollaboratorEarnings,
  });

  // Process earnings data when API data changes
  useEffect(() => {
    if (!allEarningsData || isLoadingEarnings) {
      setIsLoading(isLoadingEarnings);
      return;
    }

    const processEarningsData = async () => {
      setIsLoading(true);
      try {
        if (allEarningsData.length === 0) {
          setEarnings([]);
          setFilteredEarnings([]);
          setCollaborators([]);
          setIsLoading(false);
          return;
        }

        // Get unique collaborator and customer IDs
        const collaboratorIds = [
          ...new Set(allEarningsData.map((e) => e.collaborator_id)),
        ];
        const customerIds = [
          ...new Set(
            allEarningsData
              .map((e) => e.user_subscriptions?.user_id)
              .filter(Boolean)
          ),
        ];

        // Fetch collaborator profiles
        const { data: collaboratorProfiles, error: collaboratorError } =
          await supabase
            .from("profiles")
            .select("id, name, email, avatar_url")
            .in("id", collaboratorIds);

        if (collaboratorError) throw collaboratorError;

        // Fetch customer profiles
        const { data: customerProfiles, error: customerError } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", customerIds);

        if (customerError) throw customerError;

        // Fetch payout information
        const payoutIds = allEarningsData
          .map((e) => e.payout_id)
          .filter(Boolean);
        let payoutData: Array<{
          id: number;
          status: "pending" | "completed" | "failed";
          payout_date: string | null;
        }> = [];
        if (payoutIds.length > 0) {
          const { data: payouts, error: payoutError } = await supabase
            .from("collaborator_payouts")
            .select("id, status, payout_date")
            .in("id", payoutIds);

          if (payoutError) throw payoutError;
          payoutData = payouts || [];
        }

        // Create lookup maps
        const collaboratorMap = Object.fromEntries(
          (collaboratorProfiles || []).map((c) => [c.id, c])
        );
        const customerMap = Object.fromEntries(
          (customerProfiles || []).map((c) => [c.id, c])
        );
        const payoutMap = Object.fromEntries(payoutData.map((p) => [p.id, p]));

        // Combine all data
        const enrichedEarnings: CollaboratorEarning[] = allEarningsData.map(
          (earning) => ({
            ...earning,
            collaborator: collaboratorMap[earning.collaborator_id],
            promotion: earning.promotions,
            user_subscription: earning.user_subscriptions,
            customer: earning.user_subscriptions?.user_id
              ? customerMap[earning.user_subscriptions.user_id]
              : null,
            payout: earning.payout_id
              ? payoutMap[earning.payout_id]
              : undefined,
          })
        );

        setEarnings(enrichedEarnings);
        setFilteredEarnings(enrichedEarnings);

        // Set collaborators for filter
        setCollaborators(
          (collaboratorProfiles || []).map((c) => ({ id: c.id, name: c.name }))
        );
      } catch (error) {
        console.error("Error processing earnings data:", error);
        toast({
          title: "Error",
          description: "Failed to process earnings data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    processEarningsData();
  }, [allEarningsData, isLoadingEarnings, toast]);

  // Skip page reset on first render (preserves restored page from sessionStorage)
  const isFirstRender = useRef(true);

  // Apply filters
  useEffect(() => {
    let filtered = earnings;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (earning) =>
          earning.collaborator?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          earning.collaborator?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          earning.promotion?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          earning.promotion?.code
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          earning.customer?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          earning.customer?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((earning) => {
        switch (statusFilter) {
          case "pending":
            return !earning.payout_id;
          case "processing":
            return earning.payout?.status === "pending";
          case "paid":
            return earning.payout?.status === "completed";
          case "failed":
            return earning.payout?.status === "failed";
          default:
            return true;
        }
      });
    }

    // Collaborator filter
    if (collaboratorFilter !== "all") {
      filtered = filtered.filter(
        (earning) => earning.collaborator_id.toString() === collaboratorFilter
      );
    }

    setFilteredEarnings(filtered);
    // Reset to first page when filters change (skip on first render)
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, collaboratorFilter, earnings]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  // Get earning status
  const getEarningStatus = (earning: CollaboratorEarning) => {
    if (!earning.payout_id) {
      return {
        status: "pending",
        label: "Pending",
        className: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      };
    }

    switch (earning.payout?.status) {
      case "pending":
        return {
          status: "processing",
          label: "Processing",
          className: "bg-blue-500/10 text-blue-500 border-blue-500/20",
        };
      case "completed":
        return {
          status: "paid",
          label: "Paid",
          className: "bg-green-500/10 text-green-500 border-green-500/20",
        };
      case "failed":
        return {
          status: "failed",
          label: "Failed",
          className: "bg-red-500/10 text-red-500 border-red-500/20",
        };
      default:
        return {
          status: "unknown",
          label: "Unknown",
          className: "bg-gray-500/10 text-gray-500 border-gray-500/20",
        };
    }
  };

  // Pagination
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredEarnings.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  const paginatedEarnings = filteredEarnings.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (isLoading) {
    return (
      <AdminLayout title="Collaborator Earnings">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Collaborator Earnings">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Collaborator Earnings</h1>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Earnings
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(earningsOverview?.totalEarnings || 0)}
                  </p>
                </div>
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Pending</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(earningsOverview?.pendingEarnings || 0)}
                  </p>
                </div>
                <Calendar className="h-5 w-5 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Paid Out</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(earningsOverview?.paidEarnings || 0)}
                  </p>
                </div>
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Transactions
                  </p>
                  <p className="text-2xl font-bold">
                    {earningsOverview?.totalTransactions || 0}
                  </p>
                </div>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search earnings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={collaboratorFilter}
            onValueChange={setCollaboratorFilter}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by collaborator" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Collaborators</SelectItem>
              {collaborators.map((collaborator) => (
                <SelectItem
                  key={collaborator.id}
                  value={collaborator.id.toString()}
                >
                  {collaborator.name || `User ${collaborator.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>

        {/* Earnings Table */}
        <Card>
          <CardHeader>
            <CardTitle>Commission Logs</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredEarnings.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Earnings Found</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {earnings.length === 0
                    ? "No collaborator earnings have been generated yet."
                    : "No earnings match your current filters."}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Collaborator</TableHead>
                        <TableHead>Promotion</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Commission</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedEarnings.map((earning) => {
                        const { status, label, className } =
                          getEarningStatus(earning);
                        return (
                          <TableRow key={earning.id}>
                            <TableCell>
                              <span className="text-sm">
                                {format(
                                  new Date(earning.created_at),
                                  "MMM dd, yyyy HH:mm"
                                )}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="w-6 h-6">
                                  <AvatarImage
                                    src={
                                      earning.collaborator?.avatar_url ||
                                      undefined
                                    }
                                  />
                                  <AvatarFallback className="text-xs">
                                    {earning.collaborator?.name?.charAt(0) ||
                                      "U"}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="text-sm font-medium">
                                    {earning.collaborator?.name || "Unknown"}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {earning.collaborator?.email}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="text-sm font-medium">
                                  {earning.promotion?.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {earning.promotion?.code}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="text-sm">
                                  {earning.customer?.name || "Unknown"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {earning.customer?.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {earning.commission_type_at_time_of_transaction ===
                                "percentage"
                                  ? `${earning.commission_at_time_of_transaction}%`
                                  : formatCurrency(
                                      earning.commission_at_time_of_transaction
                                    )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {earning.commission_type_at_time_of_transaction}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(earning.amount_earned)}
                            </TableCell>
                            <TableCell>
                              <Badge className={className}>{label}</Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/admin/collaborator/${earning.collaborator_id}`
                                  )
                                }
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {filteredEarnings.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {filteredEarnings.length === 0
                          ? 0
                          : (currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          currentPage * pageSize,
                          filteredEarnings.length
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {filteredEarnings.length}
                      </span>{" "}
                      earnings
                    </div>
                    <div className="flex items-center gap-4">
                      {totalPages > 0 && (
                        <div className="flex items-center">
                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            disabled={!canGoPrev}
                          >
                            First
                          </Button>

                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(
                                currentPage > 1 ? currentPage - 1 : 1
                              )
                            }
                            disabled={!canGoPrev}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>

                          {pageNumbers.map((pageNum) => (
                            <Button
                              key={pageNum}
                              className="mx-0.5 w-8 h-8 p-0"
                              variant={
                                pageNum === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          ))}

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(
                                currentPage < totalPages
                                  ? currentPage + 1
                                  : totalPages
                              )
                            }
                            disabled={!canGoNext}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={!canGoNext}
                          >
                            Last
                          </Button>
                        </div>
                      )}

                      <select
                        className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page when changing page size
                        }}
                      >
                        {[10, 20, 30, 50].map((size) => (
                          <option key={size} value={size}>
                            {size} per page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CollaboratorEarningsList;
 